import { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { http, currency, compact, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Header from "@/components/Header";
import { Ban, CheckCircle, Star, StarOff, Trash2, RefreshCcw, Users, DollarSign, Rocket, FlaskConical } from "lucide-react";

export default function Admin() {
  const { user } = useAuth();
  const [overview, setOverview] = useState(null);
  const [users, setUsers] = useState([]);
  const [pixels, setPixels] = useState([]);
  const [tab, setTab] = useState("overview");

  const load = async () => {
    try {
      const [o, u, p] = await Promise.all([
        http.get("/admin/overview"),
        http.get("/admin/users"),
        http.get("/admin/pixels"),
      ]);
      setOverview(o.data);
      setUsers(u.data.users || []);
      setPixels(p.data.blocks || []);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  useEffect(() => { if (user && user.role === "admin") load(); }, [user]);

  if (user === null) return <div className="pt-40 text-center">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/" replace />;

  const ban = async (uid, banned) => {
    await http.post(`/admin/users/${uid}/ban`, null, { params: { banned } });
    toast.success(banned ? "User banned" : "User unbanned");
    load();
  };
  const feature = async (pid, featured) => {
    await http.post(`/admin/pixels/${pid}/feature`, null, { params: { featured } });
    load();
  };
  const remove = async (p) => {
    const label = `"${p.brand_name || "Untitled"}" at (${p.x},${p.y}) · ${p.width}×${p.height}`;
    if (!window.confirm(
      `Delete block ${label}?\n\n` +
      "The grid cells will become claimable again. This cannot be undone."
    )) return;
    try {
      await http.delete(`/admin/pixels/${p.id}`);
      toast.success(`Deleted ${p.brand_name || "block"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const refund = async (pid) => {
    if (!window.confirm("Refund this purchase?")) return;
    try {
      await http.post(`/admin/pixels/${pid}/refund`);
      toast.success("Refund issued"); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const purgeTestBlocks = async () => {
    try {
      const preview = await http.post("/admin/pixels/purge-test", null, { params: { confirm: false } });
      const { count, sample } = preview.data;
      if (count === 0) { toast.info("No TEST_ blocks found — grid is clean."); return; }
      const names = (sample || []).map((b) => b.brand_name).slice(0, 5).join(", ");
      const ok = window.confirm(`Delete ${count} test block${count === 1 ? "" : "s"}?\n\nExamples: ${names}${count > 5 ? "…" : ""}`);
      if (!ok) return;
      const { data } = await http.post("/admin/pixels/purge-test", null, { params: { confirm: true } });
      toast.success(`Deleted ${data.deleted} test block${data.deleted === 1 ? "" : "s"}`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <main className="pt-32 pb-24 px-4 max-w-6xl mx-auto space-y-6">
        <div>
          <div className="pill"><span>Admin</span></div>
          <h1 className="mt-2 font-display text-4xl">Control room</h1>
        </div>

        {overview && (
          <>
            <section data-testid="admin-overview" className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <Metric icon={DollarSign} label="Revenue" value={currency(overview.revenue)} tint="from-violet-500 to-fuchsia-500" testid="metric-revenue" />
              <Metric icon={Users} label="Users" value={compact(overview.users)} sub={`${overview.banned} banned`} tint="from-cyan-500 to-teal-500" testid="metric-users" />
              <Metric label="Blocks" value={compact(overview.blocks_paid)} sub={`${overview.blocks_reserved} reserved`} tint="from-fuchsia-500 to-pink-500" testid="metric-blocks" />
              <Metric icon={Rocket} label="Promo" value={`${overview.promo?.used}/${overview.promo?.total}`} sub={overview.promo?.is_free_phase ? "Active" : "Ended"} tint="from-amber-500 to-orange-500" testid="metric-promo" />
            </section>
          </>
        )}

        <div className="flex gap-1 p-1 glass w-fit">
          {["overview", "users", "pixels"].map((t) => (
            <button
              key={t} data-testid={`tab-${t}`} onClick={() => setTab(t)}
              className={`px-4 py-1.5 rounded-full text-sm capitalize transition ${
                tab === t ? "bg-white text-black" : "hover:bg-white/10"
              }`}
            >{t}</button>
          ))}
          <button onClick={load} className="btn-ghost px-3 py-1.5"><RefreshCcw className="w-3.5 h-3.5" /></button>
        </div>

        {tab === "users" && (
          <section data-testid="users-table" className="card-elev p-4 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-[var(--fg-muted)]">
                  <th className="py-2">Email</th><th>Name</th><th>Role</th><th>Status</th><th></th>
                </tr>
              </thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="border-t border-[var(--line-strong)]">
                    <td className="py-2 font-mono text-xs">{u.email}</td>
                    <td>{u.name || u.brand_name || "—"}</td>
                    <td><span className="pill">{u.role}</span></td>
                    <td>{u.banned ? <span className="pill" style={{background:"rgba(239,68,68,0.15)",color:"#f87171",borderColor:"rgba(239,68,68,0.5)"}}>banned</span> : <span className="pill" style={{background:"rgba(34,197,94,0.15)",color:"#4ade80",borderColor:"rgba(34,197,94,0.5)"}}>active</span>}</td>
                    <td className="text-right">
                      <button data-testid={`ban-${u.id}`} onClick={() => ban(u.id, !u.banned)} className="btn-ghost">
                        {u.banned ? <><CheckCircle className="w-4 h-4" /> Unban</> : <><Ban className="w-4 h-4" /> Ban</>}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}

        {tab === "pixels" && (
          <section data-testid="pixels-table" className="card-elev p-4 overflow-x-auto">
            <div className="flex items-center justify-between mb-3">
              <div className="text-xs text-[var(--fg-muted)]">{pixels.length} block{pixels.length === 1 ? "" : "s"} total</div>
              <button
                data-testid="purge-test-blocks"
                onClick={purgeTestBlocks}
                className="btn-ghost text-amber-300 border-amber-400/30"
                title="Delete every block whose brand starts with TEST_"
              >
                <FlaskConical className="w-4 h-4" /> Purge TEST_ blocks
              </button>
            </div>
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs uppercase text-[var(--fg-muted)]">
                  <th className="py-2">Block</th><th>Brand</th><th>Status</th><th>Tier</th><th>Amount</th><th>Clicks</th><th></th>
                </tr>
              </thead>
              <tbody>
                {pixels.map((p) => (
                  <tr key={p.id} className="border-t border-[var(--line-strong)]">
                    <td className="py-2 font-mono text-xs">({p.x},{p.y}) · {p.width}×{p.height}</td>
                    <td>{p.brand_name || "—"}</td>
                    <td><span className="pill">{p.payment_status}{p.is_free ? " · free" : ""}</span></td>
                    <td>{p.tier}</td>
                    <td className="font-mono">{currency(p.amount || 0)}</td>
                    <td className="font-mono">{p.click_count || 0}</td>
                    <td className="text-right space-x-1">
                      <button data-testid={`feature-${p.id}`} onClick={() => feature(p.id, !p.featured)} className="btn-ghost">
                        {p.featured ? <StarOff className="w-4 h-4" /> : <Star className="w-4 h-4" />}
                      </button>
                      <button data-testid={`refund-${p.id}`} onClick={() => refund(p.id)} className="btn-ghost">
                        <RefreshCcw className="w-4 h-4" />
                      </button>
                      <button data-testid={`delete-${p.id}`} onClick={() => remove(p)} className="btn-ghost" title="Delete this brand's block">
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        )}
      </main>
    </div>
  );
}

function Metric({ icon: Icon, label, value, sub, tint, testid }) {
  return (
    <div data-testid={testid} className="glass p-5">
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-[var(--fg-muted)]">
        <span className={`w-1.5 h-6 rounded-full bg-gradient-to-b ${tint}`} />
        {Icon && <Icon className="w-3.5 h-3.5" />} {label}
      </div>
      <div className="mt-2 text-3xl font-display font-medium">{value}</div>
      {sub && <div className="mt-1 text-xs text-[var(--fg-muted)]">{sub}</div>}
    </div>
  );
}
